import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const base = `/${lang || "ar"}`;

  const switchLang = (target) => {
    if (target === lang) return;
    const pathParts = location.pathname.split("/").filter(Boolean);
    if (pathParts.length && (pathParts[0] === "ar" || pathParts[0] === "en")) {
      pathParts[0] = target;
    } else {
      pathParts.unshift(target);
    }
    navigate("/" + pathParts.join("/"), { replace: false });
    i18n.changeLanguage(target);
  };

  const scrollToAnchor = (id) => (e) => {
    if (location.pathname === base || location.pathname === base + "/") {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
      setOpen(false);
    } else {
      // navigate home with hash
      navigate(base + "#" + id);
      setOpen(false);
    }
  };

  return (
    <header className={`fp-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="fp-header__inner">
        <NavLink to={base} className="fp-header__brand" onClick={() => setOpen(false)}>
          <Logo />
        </NavLink>

        <nav className={`fp-nav ${open ? "is-open" : ""}`} aria-label="primary">
          <ul className="fp-nav__list">
            <li><a href={base + "#top"} onClick={scrollToAnchor("top")}>{t("header.nav.home")}</a></li>
            <li><a href={base + "#about"} onClick={scrollToAnchor("about")}>{t("header.nav.about")}</a></li>
            <li><a href={base + "#how"} onClick={scrollToAnchor("how")}>{t("header.nav.how")}</a></li>
            <li><a href={base + "#benefits"} onClick={scrollToAnchor("benefits")}>{t("header.nav.benefits")}</a></li>
            <li><a href={base + "#faq"} onClick={scrollToAnchor("faq")}>{t("header.nav.faq")}</a></li>
            <li><a href={base + "#contact"} onClick={scrollToAnchor("contact")}>{t("header.nav.contact")}</a></li>
          </ul>

          <div className="fp-nav__actions">
            <div className="fp-lang" role="group" aria-label={t("lang.switch_to")}>
              <button
                type="button"
                className={`fp-lang__btn ${lang === "ar" ? "is-active" : ""}`}
                onClick={() => switchLang("ar")}
                aria-pressed={lang === "ar"}
              >ES</button>
              <button
                type="button"
                className={`fp-lang__btn ${lang === "en" ? "is-active" : ""}`}
                onClick={() => switchLang("en")}
                aria-pressed={lang === "en"}
              >EN</button>
            </div>
            <a href={base + "#contact"} onClick={scrollToAnchor("contact")} className="fp-btn fp-btn--primary fp-btn--sm">
              {t("header.cta")}
            </a>
          </div>
        </nav>

        <button
          className={`fp-burger ${open ? "is-open" : ""}`}
          aria-label={open ? t("header.menu_close") : t("header.menu_open")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span /><span /><span />
        </button>
      </div>
    </header>
  );
}
